import {
    AutoAwesome as AutoAwesomeIcon,
    CheckCircle as CheckCircleIcon,
    CompareArrows as CompareIcon,
    Description as DescriptionIcon,
    Download as DownloadIcon,
    History as HistoryIcon,
    Info as InfoIcon,
    Person as PersonIcon,
    PictureAsPdf as PictureAsPdfIcon,
    RestoreFromTrash as RestoreIcon,
    TextSnippet as TextSnippetIcon,
    Upload as UploadIcon,
} from '@mui/icons-material';
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    Grid,
    InputLabel,
    Menu,
    MenuItem,
    Paper,
    Select,
    Stack,
    Tab,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tabs,
    Tooltip,
    Typography,
} from '@mui/material';
import axios from 'axios';
import { formatDistanceToNow } from 'date-fns';
import { useEffect, useMemo, useState } from 'react';
import textFileServiceInstance from '../../services/TextFileService';
import { getProcessStageColor } from '../../utils/processStageUtils';
import { downloadTextFile } from '../../utils/textFileDownloadUtils';
import TextDiffViewer, { computeLineDiff } from '../diff/TextDiffViewer';
import { useTrackUsage } from '../../hooks/useTrackUsage';

const SAVE_TYPES = {
    INITIAL_CREATION: { label: 'AI Generated', icon: AutoAwesomeIcon, color: '#7c4dff', bgColor: 'rgba(124, 77, 255, 0.08)' },
    AUTO: { label: 'Auto Save', icon: AutoAwesomeIcon, color: '#4caf50', bgColor: 'rgba(76, 175, 80, 0.08)' },
    MANUAL: { label: 'Manual Save', icon: PersonIcon, color: '#2196f3', bgColor: 'rgba(33, 150, 243, 0.08)' },
    IMPORT: { label: 'Imported', icon: UploadIcon, color: '#9c27b0', bgColor: 'rgba(156, 39, 176, 0.08)' },
    RESTORE: { label: 'Restored', icon: RestoreIcon, color: '#ff9800', bgColor: 'rgba(255, 152, 0, 0.08)' },
    STAGE_TRANSITION: { label: 'Stage Transition', icon: HistoryIcon, color: '#607d8b', bgColor: 'rgba(96, 125, 139, 0.08)' },
};

const PROCESS_STAGES = [
    'LEVEL1_WIP',
    'LEVEL1_SEVAK_COMPLETE',
    'LEVEL2_WIP',
    'LEVEL2_SEVAK_COMPLETE',
    'LEVEL3_WIP',
    'LEVEL3_SEVAK_COMPLETE',
    'PUBLISHED',
];

const SELECTABLE_PROCESS_STAGES = PROCESS_STAGES.filter((stage) => !stage.endsWith('_WIP'));

const formatTime = (timestamp) => {
    if (!timestamp) return { relative: 'Unknown', full: 'Unknown date' };
    try {
        const date = new Date(timestamp);
        return {
            relative: formatDistanceToNow(date, { addSuffix: true }),
            full: date.toLocaleString(),
        };
    } catch (e) {
        return { relative: 'Unknown', full: 'Unknown date' };
    }
};

const formatProcessStage = (stage) => {
    if (!stage) return 'Unknown';
    let formatted = stage
        .replace('_WIP', ' (In Progress)')
        .replace('_SEVAK_COMPLETE', ' (Complete)');
    const levelMatch = formatted.match(/LEVEL(\d+)/);
    if (levelMatch) {
        return `L${levelMatch[1]}${formatted.split('LEVEL' + levelMatch[1])[1]}`;
    }
    return formatted;
};

const getSaveTypeIcon = (saveType) => {
    const type = SAVE_TYPES[saveType] || { icon: HistoryIcon, color: '#757575', label: saveType || 'Unknown' };
    const IconComponent = type.icon;
    return (
        <Tooltip title={type.label}>
            <IconComponent fontSize="small" sx={{ color: type.color, verticalAlign: 'middle' }} />
        </Tooltip>
    );
};

const TextFileDetailsDialog = ({ open, onClose, textFile, onUpdated }) => {
  useTrackUsage('TextFileDetailsDialog', 'dialog', 'folder-browser', open);
    const [activeLanguage, setActiveLanguage] = useState('');
    const [historyByLanguage, setHistoryByLanguage] = useState({});
    const [loadingHistory, setLoadingHistory] = useState(false);
    const [savingStage, setSavingStage] = useState(false);
    const [restoringVersion, setRestoringVersion] = useState(null);
    const [confirmingRestore, setConfirmingRestore] = useState(false);
    const [restoring, setRestoring] = useState(false);
    const [restoreSuccess, setRestoreSuccess] = useState(null);
    const [restoreError, setRestoreError] = useState(null);
    const [fileState, setFileState] = useState(textFile);

    const [downloading, setDownloading] = useState(false);
    const [downloadMenuAnchorEl, setDownloadMenuAnchorEl] = useState(null);
    const [pendingDownloadVersion, setPendingDownloadVersion] = useState(null);

    // Comparison state
    const [comparing, setComparing] = useState(false);
    const [compareVersions, setCompareVersions] = useState([]);
    const [diffState, setDiffState] = useState({ diff: null, isLoading: false, error: null, older: null, newer: null });

    const languages = useMemo(() => Object.keys(fileState?.translations || {}), [fileState]);
    const activeLanguageData = activeLanguage ? fileState?.translations?.[activeLanguage] : null;
    const activeHistory = activeLanguage ? historyByLanguage[activeLanguage] : null;
    const hasActiveHistory = Boolean(activeHistory?.versions?.length);

    useEffect(() => {
        if (!open) {
            return;
        }
        setFileState(textFile || null);
        setHistoryByLanguage({});
        setComparing(false);
        setCompareVersions([]);
        setDiffState({ diff: null, isLoading: false, error: null, older: null, newer: null });
        const languageList = Object.keys(textFile?.translations || {});
        setActiveLanguage((current) => current || languageList[0] || '');
    }, [open, textFile]);

    useEffect(() => {
        if (!open || !fileState?.id || !activeLanguage || activeLanguage in historyByLanguage) {
            return;
        }

        const fetchHistory = async () => {
            try {
                setLoadingHistory(true);
                const history = await textFileServiceInstance.getTextFileLanguageHistory(fileState.id, activeLanguage);
                setHistoryByLanguage((prev) => ({ ...prev, [activeLanguage]: history }));
            } catch (error) {
                console.error('Failed to load text file history', error);
                setHistoryByLanguage((prev) => ({ ...prev, [activeLanguage]: null }));
            } finally {
                setLoadingHistory(false);
            }
        };

        fetchHistory();
    }, [open, fileState, activeLanguage, historyByLanguage]);

    const refreshFile = async () => {
        if (!fileState?.id) {
            return;
        }
        const latest = await textFileServiceInstance.getTextFileById(fileState.id);
        setFileState(latest);
        onUpdated?.(latest);
    };

    const handleStageChange = async (newStage) => {
        if (!fileState?.id || !activeLanguage) {
            return;
        }
        if (newStage?.endsWith('_WIP')) {
            return;
        }
        try {
            setSavingStage(true);
            await textFileServiceInstance.updateTextFileTranslationStage(fileState.id, activeLanguage, newStage);
            await refreshFile();
        } catch (error) {
            console.error('Failed to update text file stage', error);
        } finally {
            setSavingStage(false);
        }
    };

    const handleRestore = (version) => {
        setRestoringVersion(version);
        setConfirmingRestore(true);
        setRestoreError(null);
    };

    const confirmRestore = async () => {
        if (!restoringVersion || !fileState?.id || !activeLanguage) return;
        setConfirmingRestore(false);
        setRestoring(true);
        setRestoreError(null);
        try {
            await textFileServiceInstance.restoreTextFileLanguageVersion(
                fileState.id,
                activeLanguage,
                restoringVersion.versionNumber,
            );
            setHistoryByLanguage((prev) => ({ ...prev, [activeLanguage]: null }));
            await refreshFile();
            setRestoreSuccess({ version: restoringVersion.versionNumber });
            setTimeout(() => setRestoreSuccess(null), 5000);
        } catch (error) {
            console.error('Failed to restore text file version', error);
            setRestoreError(`Failed to restore: ${error.response?.data?.message || error.message}`);
        } finally {
            setRestoring(false);
            setRestoringVersion(null);
        }
    };

    const cancelRestore = () => {
        setConfirmingRestore(false);
        setRestoringVersion(null);
    };

    const openDownloadMenu = (event, version) => {
        setDownloadMenuAnchorEl(event.currentTarget);
        setPendingDownloadVersion(version);
    };

    const closeDownloadMenu = () => {
        setDownloadMenuAnchorEl(null);
        setPendingDownloadVersion(null);
    };

    const handleDownload = async (format) => {
        closeDownloadMenu();
        if (!pendingDownloadVersion?.fileUrl) return;
        setDownloading(true);
        try {
            const baseName = `${fileState?.name || 'file'}_${activeLanguage}_v${pendingDownloadVersion.versionNumber}`;
            await downloadTextFile(pendingDownloadVersion.fileUrl, baseName, format);
        } catch (err) {
            console.error('Failed to download:', err);
        } finally {
            setDownloading(false);
        }
    };

    // ── Comparison handlers ──────────────────────────────────────────────────

    const toggleVersionForComparison = (version) => {
        if (!version) return;
        const isSelected = compareVersions.some((v) => v.versionNumber === version.versionNumber);
        if (isSelected) {
            setCompareVersions(compareVersions.filter((v) => v.versionNumber !== version.versionNumber));
        } else if (compareVersions.length < 2) {
            setCompareVersions([...compareVersions, version]);
        } else {
            setCompareVersions([compareVersions[1], version]);
        }
    };

    const handleCompare = async () => {
        if (compareVersions.length !== 2) return;
        const sorted = [...compareVersions].sort((a, b) => a.versionNumber - b.versionNumber);
        const [older, newer] = sorted;

        if (!older.fileUrl || !newer.fileUrl) {
            setDiffState({ diff: null, isLoading: false, error: 'One or both selected versions do not have a file URL.', older, newer });
            setComparing(true);
            return;
        }

        setDiffState({ diff: null, isLoading: true, error: null, older, newer });
        setComparing(true);

        try {
            const [olderRes, newerRes] = await Promise.all([
                axios.get(older.fileUrl),
                axios.get(newer.fileUrl),
            ]);
            const diff = computeLineDiff(String(olderRes.data), String(newerRes.data));
            setDiffState({ diff, isLoading: false, error: null, older, newer });
        } catch (err) {
            setDiffState({ diff: null, isLoading: false, error: `Failed to load file content: ${err.message || 'Unknown error'}`, older, newer });
        }
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth
            maxWidth="lg"
            scroll="paper"
            sx={{ '& .MuiPaper-root': { borderRadius: '8px' } }}
        >
            <DialogTitle sx={{ borderBottom: '1px solid', borderColor: 'divider', px: 3, py: 2 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Box>
                        <Typography variant="h6" fontWeight={600}>
                            <HistoryIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                            File Details
                        </Typography>
                        <Typography variant="subtitle2" color="text.secondary" mt={0.5}>
                            {fileState?.name || 'Text File'} – {fileState?.id || 'N/A'}
                        </Typography>
                    </Box>
                    <Chip
                        size="small"
                        variant="outlined"
                        label={`${languages.length} language${languages.length !== 1 ? 's' : ''}`}
                        sx={{ color: 'secondary.main' }}
                    />
                </Stack>
            </DialogTitle>

            <DialogContent sx={{ px: 3, py: 2 }}>
                {!fileState ? (
                    <Typography variant="body2" color="text.secondary">No file details available.</Typography>
                ) : (
                    <>
                        {/* File-level details */}
                        <Card
                            variant="outlined"
                            sx={{ mb: 2, borderRadius: '8px', backgroundColor: 'rgba(25, 118, 210, 0.03)' }}
                        >
                            <CardContent sx={{ p: 2 }}>
                                <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
                                    Text File Details
                                </Typography>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} md={6}>
                                        <Typography variant="subtitle2" color="text.secondary">Source Language</Typography>
                                        <Typography variant="body2">{fileState.sourceLanguage || 'N/A'}</Typography>
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <Typography variant="subtitle2" color="text.secondary">Type</Typography>
                                        <Typography variant="body2">{fileState.type || 'text'}</Typography>
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <Typography variant="subtitle2" color="text.secondary">Created By</Typography>
                                        <Typography variant="body2">{fileState.createdBy || 'N/A'}</Typography>
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <Typography variant="subtitle2" color="text.secondary">Created At</Typography>
                                        <Typography variant="body2">{formatTime(fileState.createdAt).full}</Typography>
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <Typography variant="subtitle2" color="text.secondary">Updated By</Typography>
                                        <Typography variant="body2">{fileState.updatedBy || 'N/A'}</Typography>
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <Typography variant="subtitle2" color="text.secondary">Updated At</Typography>
                                        <Typography variant="body2">{formatTime(fileState.updatedAt).full}</Typography>
                                    </Grid>
                                </Grid>
                            </CardContent>
                        </Card>

                        {/* Per-language history */}
                        <Card variant="outlined" sx={{ borderRadius: '8px' }}>
                            <Box sx={{ px: 2, py: 1.25, borderBottom: '1px solid', borderColor: 'divider' }}>
                                <Typography variant="subtitle1" fontWeight={600}>
                                    Per-Language History
                                </Typography>
                            </Box>
                            <CardContent sx={{ p: 2 }}>
                                <Paper variant="outlined" sx={{ borderRadius: 1, mb: 2 }}>
                                    <Tabs
                                        value={Math.max(languages.indexOf(activeLanguage), 0)}
                                        onChange={(_, index) => {
                                            setActiveLanguage(languages[index] || '');
                                            setComparing(false);
                                            setCompareVersions([]);
                                            setDiffState({ diff: null, isLoading: false, error: null, older: null, newer: null });
                                        }}
                                        variant="scrollable"
                                        scrollButtons="auto"
                                        sx={{
                                            borderBottom: 1,
                                            borderColor: 'divider',
                                            '& .MuiTab-root': { minHeight: '44px', fontWeight: 400, textTransform: 'none' },
                                            '& .Mui-selected': { fontWeight: 600, color: 'primary.main', backgroundColor: 'rgba(25, 118, 210, 0.08)' },
                                        }}
                                    >
                                        {languages.map((lang) => (
                                            <Tab key={lang} label={lang} />
                                        ))}
                                    </Tabs>
                                </Paper>

                                {activeLanguage ? (
                                    <>
                                        {/* Language metadata row */}
                                        <Grid container spacing={2} sx={{ mb: 2 }}>
                                            <Grid item xs={12} md={4}>
                                                <FormControl size="small" fullWidth disabled={savingStage}>
                                                    <InputLabel>Process Stage</InputLabel>
                                                    <Select
                                                        label="Process Stage"
                                                        value={activeLanguageData?.processStage || 'LEVEL1_WIP'}
                                                        onChange={(e) => handleStageChange(e.target.value)}
                                                    >
                                                        {PROCESS_STAGES.map((stage) => (
                                                            <MenuItem
                                                                key={stage}
                                                                value={stage}
                                                                disabled={!SELECTABLE_PROCESS_STAGES.includes(stage)}
                                                            >
                                                                {stage}
                                                            </MenuItem>
                                                        ))}
                                                    </Select>
                                                </FormControl>
                                            </Grid>
                                            <Grid item xs={12} md={4}>
                                                <Typography variant="subtitle2" color="text.secondary">Lang Created By</Typography>
                                                <Typography variant="body2">{activeLanguageData?.createdBy || 'N/A'}</Typography>
                                            </Grid>
                                            <Grid item xs={12} md={4}>
                                                <Typography variant="subtitle2" color="text.secondary">Lang Created At</Typography>
                                                <Tooltip title={formatTime(activeLanguageData?.createdAt).relative}>
                                                    <Typography variant="body2">{formatTime(activeLanguageData?.createdAt).full}</Typography>
                                                </Tooltip>
                                            </Grid>
                                            <Grid item xs={12} md={4}>
                                                <Typography variant="subtitle2" color="text.secondary">Lang Updated By</Typography>
                                                <Typography variant="body2">{activeLanguageData?.updatedBy || 'N/A'}</Typography>
                                            </Grid>
                                            <Grid item xs={12} md={4}>
                                                <Typography variant="subtitle2" color="text.secondary">Lang Updated At</Typography>
                                                <Tooltip title={formatTime(activeLanguageData?.updatedAt).relative}>
                                                    <Typography variant="body2">{formatTime(activeLanguageData?.updatedAt).full}</Typography>
                                                </Tooltip>
                                            </Grid>
                                            <Grid item xs={12} md={4}>
                                                <Typography variant="subtitle2" color="text.secondary">Status</Typography>
                                                <Typography variant="body2">{activeLanguageData?.translationStatus || 'N/A'}</Typography>
                                            </Grid>
                                        </Grid>

                                        {/* Info row + comparison controls above table */}
                                        {hasActiveHistory && (
                                            <Box
                                                sx={{
                                                    px: 2, py: 1.5, mb: 0,
                                                    borderRadius: '8px 8px 0 0',
                                                    border: '1px solid',
                                                    borderColor: 'divider',
                                                    borderBottom: 'none',
                                                    backgroundColor: 'rgba(25, 118, 210, 0.04)',
                                                }}
                                            >
                                                {/* Language info row */}
                                                <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1.5}>
                                                    <Box display="flex" alignItems="center">
                                                        <InfoIcon fontSize="small" sx={{ mr: 1, color: 'primary.main' }} />
                                                        <Typography variant="subtitle2" fontWeight={500}>
                                                            {activeLanguage}
                                                        </Typography>
                                                    </Box>
                                                    <Typography variant="body2" color="text.secondary">
                                                        {activeHistory.versions.length} version{activeHistory.versions.length !== 1 ? 's' : ''}
                                                    </Typography>
                                                </Stack>

                                                {/* Comparison controls row */}
                                                <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
                                                    <Stack direction="row" spacing={1} alignItems="center">
                                                        <CompareIcon color={comparing ? 'primary' : 'action'} fontSize="small" />
                                                        <Typography variant="body2" fontWeight={500} color={comparing ? 'primary' : 'text.primary'}>
                                                            {comparing ? 'Comparing Versions' : 'Select up to 2 versions to compare'}
                                                        </Typography>
                                                    </Stack>

                                                    <Stack direction="row" spacing={1} alignItems="center">
                                                        {compareVersions.map((v, idx) => (
                                                            <Chip
                                                                key={v.versionNumber}
                                                                label={`v${v.versionNumber}`}
                                                                onDelete={() => toggleVersionForComparison(v)}
                                                                size="small"
                                                                variant={idx === 0 ? 'outlined' : 'filled'}
                                                                sx={{ color: 'secondary.main' }}
                                                            />
                                                        ))}
                                                        {compareVersions.length > 0 && (
                                                            <Button
                                                                variant="contained"
                                                                disableElevation
                                                                size="small"
                                                                disabled={compareVersions.length !== 2}
                                                                onClick={handleCompare}
                                                                startIcon={<CompareIcon />}
                                                                color="primary"
                                                                sx={{ textTransform: 'none' }}
                                                            >
                                                                Compare
                                                            </Button>
                                                        )}
                                                    </Stack>
                                                </Stack>
                                            </Box>
                                        )}

                                        {/* Comparison view */}
                                        {comparing && (
                                            <Card
                                                variant="outlined"
                                                sx={{ mb: 2, borderRadius: '0 0 8px 8px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}
                                            >
                                                <CardContent sx={{ p: 0 }}>
                                                    <Box
                                                        sx={{
                                                            p: 2,
                                                            borderBottom: '1px solid',
                                                            borderColor: 'divider',
                                                            backgroundColor: 'rgba(33,150,243,0.04)',
                                                            display: 'flex',
                                                            justifyContent: 'space-between',
                                                            alignItems: 'center',
                                                        }}
                                                    >
                                                        <Typography variant="subtitle1" fontWeight={500}>
                                                            Comparing: v{diffState.older?.versionNumber} vs v{diffState.newer?.versionNumber}
                                                        </Typography>
                                                        <Button
                                                            size="small"
                                                            variant="outlined"
                                                            color="inherit"
                                                            onClick={() => setComparing(false)}
                                                            sx={{ textTransform: 'none' }}
                                                        >
                                                            Close Comparison
                                                        </Button>
                                                    </Box>
                                                    <Box sx={{ pt: 3, px: 3, pb: 4 }}>
                                                        <TextDiffViewer
                                                            diff={diffState.diff}
                                                            isLoading={diffState.isLoading}
                                                            error={diffState.error}
                                                            olderVersion={diffState.older}
                                                            newerVersion={diffState.newer}
                                                            formatTime={formatTime}
                                                        />
                                                    </Box>
                                                </CardContent>
                                            </Card>
                                        )}

                                        {/* History table */}
                                        {loadingHistory ? (
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 3 }}>
                                                <CircularProgress size={20} />
                                                <Typography variant="body2" color="text.secondary">Loading history…</Typography>
                                            </Box>
                                        ) : !hasActiveHistory ? (
                                            <Card
                                                variant="outlined"
                                                sx={{ borderRadius: '8px', backgroundColor: 'rgba(25, 118, 210, 0.03)', borderStyle: 'dashed' }}
                                            >
                                                <CardContent sx={{ p: 2 }}>
                                                    <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                                                        No saved history yet
                                                    </Typography>
                                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                                                        This is expected for legacy text files, or for files that have not been saved since text history tracking was added.
                                                    </Typography>
                                                    <Alert severity="info" sx={{ alignItems: 'center' }}>
                                                        Metadata and process stage are still available. The next save will create the first restore point for this language.
                                                    </Alert>
                                                </CardContent>
                                            </Card>
                                        ) : (
                                            <TableContainer
                                                component={Paper}
                                                variant="outlined"
                                                sx={{ borderRadius: hasActiveHistory ? '0 0 8px 8px' : '8px' }}
                                            >
                                                <Table size="small" stickyHeader>
                                                    <TableHead>
                                                        <TableRow sx={{ backgroundColor: 'rgba(0,0,0,0.02)' }}>
                                                            <TableCell sx={{ fontWeight: 600 }}>Version</TableCell>
                                                            <TableCell sx={{ fontWeight: 600 }}>Save Type</TableCell>
                                                            <TableCell sx={{ fontWeight: 600 }}>Stage</TableCell>
                                                            <TableCell sx={{ fontWeight: 600 }}>Saved By</TableCell>
                                                            <TableCell sx={{ fontWeight: 600 }}>Saved At</TableCell>
                                                            <TableCell sx={{ fontWeight: 600 }} align="right">Actions</TableCell>
                                                        </TableRow>
                                                    </TableHead>
                                                    <TableBody>
                                                        {activeHistory.versions.map((version) => (
                                                            <TableRow
                                                                key={`${activeLanguage}-${version.versionNumber}`}
                                                                sx={{
                                                                    '&:hover': { backgroundColor: 'rgba(0,0,0,0.02)' },
                                                                    backgroundColor: compareVersions.some((v) => v.versionNumber === version.versionNumber)
                                                                        ? 'rgba(33,150,243,0.08)'
                                                                        : SAVE_TYPES[version.saveType]?.bgColor || 'transparent',
                                                                }}
                                                            >
                                                                <TableCell>
                                                                    <Typography variant="body2" fontWeight={500}>
                                                                        v{version.versionNumber}
                                                                    </Typography>
                                                                </TableCell>
                                                                <TableCell>
                                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                                                        {getSaveTypeIcon(version.saveType)}
                                                                        <Typography variant="caption" fontWeight={500}>
                                                                            {SAVE_TYPES[version.saveType]?.label || version.saveType || 'Unknown'}
                                                                        </Typography>
                                                                    </Box>
                                                                </TableCell>
                                                                <TableCell>
                                                                    <Chip
                                                                        label={formatProcessStage(version.processStage) || 'Unknown'}
                                                                        size="small"
                                                                        sx={{
                                                                            backgroundColor: getProcessStageColor(version.processStage),
                                                                            color: '#fff',
                                                                            fontWeight: 500,
                                                                        }}
                                                                    />
                                                                </TableCell>
                                                                <TableCell>
                                                                    <Tooltip title={version.createdBy || 'Unknown'}>
                                                                        <Typography variant="body2" noWrap>
                                                                            {version.createdBy === 'system'
                                                                                ? 'System'
                                                                                : (version.createdBy || 'Unknown').split('@')[0]}
                                                                        </Typography>
                                                                    </Tooltip>
                                                                </TableCell>
                                                                <TableCell>
                                                                    <Tooltip title={formatTime(version.createdAt).relative}>
                                                                        <Typography variant="body2">
                                                                            {formatTime(version.createdAt).full}
                                                                        </Typography>
                                                                    </Tooltip>
                                                                </TableCell>
                                                                <TableCell align="right">
                                                                    <Stack direction="row" spacing={1} justifyContent="flex-end" alignItems="center">
                                                                        {/* Download */}
                                                                        <Button
                                                                            size="small"
                                                                            variant="outlined"
                                                                            color="success"
                                                                            startIcon={<DownloadIcon />}
                                                                            onClick={(e) => openDownloadMenu(e, version)}
                                                                            disabled={!version.fileUrl || downloading || restoring || confirmingRestore}
                                                                            sx={{ textTransform: 'none' }}
                                                                        >
                                                                            Download
                                                                        </Button>

                                                                        {/* Select for comparison */}
                                                                        <Button
                                                                            size="small"
                                                                            variant={compareVersions.some((v) => v.versionNumber === version.versionNumber) ? 'contained' : 'outlined'}
                                                                            color="primary"
                                                                            disableElevation
                                                                            startIcon={<CompareIcon />}
                                                                            onClick={() => toggleVersionForComparison(version)}
                                                                            disabled={restoring || confirmingRestore || !version.fileUrl}
                                                                            sx={{ textTransform: 'none' }}
                                                                        >
                                                                            {compareVersions.some((v) => v.versionNumber === version.versionNumber) ? 'Selected' : 'Select'}
                                                                        </Button>

                                                                        {confirmingRestore && restoringVersion?.versionNumber === version.versionNumber ? (
                                                                            <>
                                                                                <Typography variant="caption" fontWeight={500} color="warning.main">
                                                                                    Restore v{version.versionNumber}?
                                                                                </Typography>
                                                                                <Button size="small" variant="contained" color="warning" onClick={confirmRestore} sx={{ minWidth: 'auto', px: 1 }}>
                                                                                    Yes
                                                                                </Button>
                                                                                <Button size="small" variant="outlined" color="inherit" onClick={cancelRestore} sx={{ minWidth: 'auto', px: 1 }}>
                                                                                    No
                                                                                </Button>
                                                                            </>
                                                                        ) : restoring && restoringVersion?.versionNumber === version.versionNumber ? (
                                                                            <Button size="small" variant="outlined" color="secondary" disabled startIcon={<CircularProgress size={16} />} sx={{ textTransform: 'none' }}>
                                                                                Restoring…
                                                                            </Button>
                                                                        ) : restoreSuccess?.version === version.versionNumber ? (
                                                                            <Button size="small" variant="contained" color="success" disableElevation startIcon={<CheckCircleIcon />} sx={{ textTransform: 'none' }}>
                                                                                Restored
                                                                            </Button>
                                                                        ) : (
                                                                            <Button
                                                                                size="small"
                                                                                variant="outlined"
                                                                                color="secondary"
                                                                                startIcon={<RestoreIcon />}
                                                                                disabled={restoring || confirmingRestore}
                                                                                onClick={() => handleRestore(version)}
                                                                                sx={{ textTransform: 'none' }}
                                                                            >
                                                                                Restore
                                                                            </Button>
                                                                        )}
                                                                        {restoreError && restoringVersion?.versionNumber === version.versionNumber && (
                                                                            <Alert severity="error" variant="outlined" sx={{ py: 0, px: 1 }} onClose={() => setRestoreError(null)}>
                                                                                {restoreError}
                                                                            </Alert>
                                                                        )}
                                                                    </Stack>
                                                                </TableCell>
                                                            </TableRow>
                                                        ))}
                                                    </TableBody>
                                                </Table>
                                            </TableContainer>
                                        )}
                                    </>
                                ) : (
                                    <Typography variant="body2" color="text.secondary">No languages available.</Typography>
                                )}
                            </CardContent>
                        </Card>
                    </>
                )}
            </DialogContent>

            <DialogActions>
                <Button onClick={onClose}>Close</Button>
            </DialogActions>

            <Menu anchorEl={downloadMenuAnchorEl} open={Boolean(downloadMenuAnchorEl)} onClose={closeDownloadMenu}>
                <MenuItem onClick={() => handleDownload('pdf')}>
                    <PictureAsPdfIcon fontSize="small" sx={{ mr: 1 }} />
                    Download as PDF
                </MenuItem>
                <MenuItem onClick={() => handleDownload('docx')}>
                    <DescriptionIcon fontSize="small" sx={{ mr: 1 }} />
                    Download as DOCX
                </MenuItem>
                <MenuItem onClick={() => handleDownload('txt')}>
                    <TextSnippetIcon fontSize="small" sx={{ mr: 1 }} />
                    Download as Text
                </MenuItem>
            </Menu>
        </Dialog>
    );
};

export default TextFileDetailsDialog;
